import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import neuralwardLogo from "../assets/neuralward-logo.svg";
// No need to import icon files since we'll use inline SVGs

const Footer = () => {
  // Animation controls for enhanced re-triggering
  const controls = useAnimation();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const linkHoverVariants = {
    hover: {
      x: 5,
      color: "#5742bb", // Brand primary color
      transition: { duration: 0.2 },
    },
  };

  const socialIconVariants = {
    hover: {
      y: -5,
      scale: 1.2,
      transition: { type: "spring", stiffness: 500 },
    },
  };

  const ctaButtonVariants = {
    hover: {
      scale: 1.05,
      backgroundColor: "#ea3d95", // Brand accent color
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.98,
    },
  };

  // Use IntersectionObserver for efficient animation triggering
  useEffect(() => {
    // Create an observer instance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // If footer is entering viewport
          if (entry.isIntersecting) {
            controls.start("visible");
          } else {
            // If footer has left viewport completely, reset for next viewing
            controls.start("hidden");
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    // Get the footer element to observe
    const footer = document.querySelector("footer");
    if (footer) {
      observer.observe(footer);
    }

    // Initial animation trigger
    controls.start("visible");

    // Cleanup observer on component unmount
    return () => {
      if (footer) {
        observer.unobserve(footer);
      }
    };
  }, [controls]); // Only re-run if controls change

  return (
    <footer className="bg-[#1e1e21] text-white py-10 overflow-hidden">
      {/* MAIN FOOTER CONTAINER */}
      <motion.div
        className="footer-container max-w-[1600px] mx-auto px-4 flex flex-wrap lg:gap-6 sm:gap-4 justify-between"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        {/* SOLUTIONS SECTION */}
        <motion.div
          className="footer-section flex-1 min-w-[135px] mb-4 lg:mb-0"
          variants={itemVariants}
        >
          <h4 className="mb-4 text-lg font-semibold">Solutions</h4>
          <ul className="space-y-2 text-white text-opacity-80">
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/solutions/neural-networks"
                  className="hover:text-brandPrimary block"
                  aria-label="View Neural Networks Solution"
                >
                  Neural Networks
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/solutions/data-processing"
                  className="hover:text-brandPrimary block"
                  aria-label="View Data Processing Solution"
                >
                  Data Processing
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/solutions/predictive-analytics"
                  className="hover:text-brandPrimary block"
                  aria-label="View Predictive Analytics Solution"
                >
                  Predictive Analytics
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/solutions/nlp"
                  className="hover:text-brandPrimary block"
                  aria-label="View NLP Solutions"
                >
                  NLP Solutions
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/deployment/on-premise"
                  className="hover:text-brandPrimary block"
                  aria-label="View AI Security Solution"
                >
                  AI Security
                </Link>
              </motion.div>
            </motion.li>
          </ul>
        </motion.div>

        <motion.div
          className="footer-section flex-1 min-w-[135px] mb-4 lg:mb-0"
          variants={itemVariants}
        >
          <h4 className="mb-4 text-lg font-semibold">Products</h4>
          <ul className="space-y-2 text-white text-opacity-80">
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/products/neuralops"
                  className="hover:text-brandPrimary block"
                  aria-label="View NeuralOps Product"
                >
                  NeuralOps
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/products/datasynapse"
                  className="hover:text-brandPrimary block"
                  aria-label="View DataSynapse Product"
                >
                  DataSynapse
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/products/secureai-vault"
                  className="hover:text-brandPrimary block"
                  aria-label="View SecureAI Vault Product"
                >
                  SecureAI Vault
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/products/intelliflow"
                  className="hover:text-brandPrimary block"
                  aria-label="View IntelliFlow Product"
                >
                  IntelliFlow
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/products/neural-sdk"
                  className="hover:text-brandPrimary block"
                  aria-label="View Neural SDK"
                >
                  Neural SDK
                </Link>
              </motion.div>
            </motion.li>
          </ul>
        </motion.div>

        <motion.div
          className="footer-section flex-1 min-w-[135px] mb-4 lg:mb-0"
          variants={itemVariants}
        >
          <h4 className="mb-4 text-lg font-semibold">Resources</h4>
          <ul className="space-y-2 text-white text-opacity-80">
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/resources/docs"
                  className="hover:text-brandPrimary block"
                  aria-label="View Documentation"
                >
                  Documentation
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/insights/case-studies"
                  className="hover:text-brandPrimary block"
                  aria-label="View Case Studies"
                >
                  Case Studies
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/resources/blog"
                  className="hover:text-brandPrimary block"
                  aria-label="Read our Blog"
                >
                  Blog
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/resources/webinars"
                  className="hover:text-brandPrimary block"
                  aria-label="Watch Webinars"
                >
                  Webinars
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/insights/ai-readiness"
                  className="hover:text-brandPrimary block"
                  aria-label="Take AI Readiness Assessment"
                >
                  AI Readiness Assessment
                </Link>
              </motion.div>
            </motion.li>
          </ul>
        </motion.div>

        <motion.div
          className="footer-section flex-1 min-w-[135px] mb-4 lg:mb-0"
          variants={itemVariants}
        >
          <h4 className="mb-4 text-lg font-semibold">Company</h4>
          <ul className="space-y-2 text-white text-opacity-80">
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/company/about"
                  className="hover:text-brandPrimary block"
                  aria-label="About Neuralward"
                >
                  About Neuralward
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/company/careers"
                  className="hover:text-brandPrimary block"
                  aria-label="View Careers"
                >
                  Careers
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/developers"
                  className="hover:text-brandPrimary block"
                  aria-label="Partner Program"
                >
                  Partner Program
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/get-started/services"
                  className="hover:text-brandPrimary block"
                  aria-label="Contact Us"
                >
                  Contact Us
                </Link>
              </motion.div>
            </motion.li>
            <motion.li variants={itemVariants}>
              <motion.div variants={linkHoverVariants} whileHover="hover">
                <Link
                  to="/resources/blog"
                  className="hover:text-brandPrimary block"
                  aria-label="Newsroom"
                >
                  Newsroom
                </Link>
              </motion.div>
            </motion.li>
          </ul>
        </motion.div>

        {/* CTA SECTION - WIDER COLUMN */}
        <motion.div
          className="trial-section flex flex-col flex-[2] min-w-[350px] mt-8 lg:mt-0"
          variants={itemVariants}
        >
          <motion.div
            className="trial-header flex justify-between items-center mb-6"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { delay: 0.5, duration: 0.8 },
              },
            }}
          >
            <motion.div
              whileHover={{
                scale: 1.05,
                filter: "brightness(1.2)",
                transition: { type: "spring", stiffness: 300, damping: 10 },
              }}
            >
              <Link
                to="/"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="flex items-center gap-3"
                aria-label="Neuralward - Back to top"
              >
                <img
                  src={neuralwardLogo}
                  alt=""
                  width="156.2"
                  height="186.8"
                  className="h-10 w-auto"
                  aria-hidden="true"
                />
                <span className="text-2xl font-extrabold tracking-tight text-white">neuralward</span>
              </Link>
            </motion.div>
            <div className="social-icons flex gap-1 md:gap-4 gap-2">
              {/* LinkedIn icon */}
              <motion.a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                variants={socialIconVariants}
                whileHover="hover"
                className="social-icon"
                aria-label="Follow Neuralward on LinkedIn"
              >
                <svg
                  className="w-6 h-6 fill-current text-gray-400 hover:text-[#0A66C2] transition-colors duration-300"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </motion.a>

              {/* X (Twitter) icon */}
              <motion.a
                href="https://x.com"
                target="_blank"
                rel="noopener noreferrer"
                variants={socialIconVariants}
                whileHover="hover"
                className="social-icon"
                aria-label="Follow Neuralward on X (Twitter)"
              >
                <svg
                  className="w-6 h-6 fill-current text-gray-400 hover:text-white transition-colors duration-300"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </motion.a>

              {/* Facebook icon */}
              <motion.a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                variants={socialIconVariants}
                whileHover="hover"
                className="social-icon"
                aria-label="Follow Neuralward on Facebook"
              >
                <svg
                  className="w-6 h-6 fill-current text-gray-400 hover:text-[#1877F2] transition-colors duration-300"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M9.03954 21.9997V13.9897H6.03954V9.98975H9.03954V6.98975C9.03954 3.98975 10.9395 2.18975 13.7395 2.18975C14.6395 2.18975 15.7395 2.28975 16.0395 2.33975V5.93975H14.2395C12.9395 5.93975 12.5395 6.53975 12.5395 7.43975V9.88975H15.9395L15.4395 13.8897H12.5395V21.9997" />
                </svg>
              </motion.a>

              {/* Instagram icon */}
              <motion.a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                variants={socialIconVariants}
                whileHover="hover"
                className="social-icon"
                aria-label="Follow Neuralward on Instagram"
              >
                <svg
                  className="w-6 h-6 fill-current text-gray-400 hover:text-[#E4405F] transition-colors duration-300"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </motion.a>
            </div>
          </motion.div>
          <motion.div
            className="trial-content flex flex-wrap items-center justify-between bg-gradient-to-br from-brandPrimary/20 to-brandAccent/5 p-6 rounded-lg"
            variants={{
              hidden: { y: 20, opacity: 0 },
              visible: {
                y: 0,
                opacity: 1,
                transition: { delay: 0.6, duration: 0.5 },
              },
            }}
          >
            <div className="left-column">
              <h4 className="text-2xl font-semibold mb-4">
                Get Started With Neuralward
              </h4>
              <p className="text-white text-opacity-80 max-w-md">
                Transform your business with our enterprise-grade AI solutions.
                Book a demo to see how our neural network technology can drive
                innovation and efficiency.
              </p>
            </div>
            <div className="right-column flex">
              <Link to="/demo">
                <motion.button
                  className="bg-brandPrimary text-white py-3 px-8 rounded-lg mt-4 md:mt-4 font-medium"
                  variants={ctaButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  aria-label="Book a Demo with our AI Experts"
                >
                  Book a Demo
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* FOOTER BOTTOM */}
      <motion.div
        className="footer-bottom mt-10 border-t border-gray-700 pt-6 max-w-[1600px] mx-auto px-4"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { delay: 0.8, duration: 0.5 },
          },
        }}
        initial="hidden"
        animate={controls}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:justify-between gap-3 w-full text-white text-opacity-80 text-sm text-center">
          <motion.div whileHover={{ x: 2, color: "#5742bb" }}>
            <Link to="/legal" className="hover:text-brandPrimary" aria-label="Read our Terms of Service">
              Terms of Service
            </Link>
          </motion.div>
          <motion.div whileHover={{ x: 2, color: "#5742bb" }}>
            <Link to="/privacy" className="hover:text-brandPrimary" aria-label="Read our Privacy Policy">
              Privacy Policy
            </Link>
          </motion.div>
          <motion.div whileHover={{ x: 2, color: "#5742bb" }}>
            <Link to="/refund-policy" className="hover:text-brandPrimary" aria-label="Read our Refund Policy">
              Refund Policy
            </Link>
          </motion.div>
          <motion.div whileHover={{ x: 2, color: "#5742bb" }}>
            <Link to="/accessibility" className="hover:text-brandPrimary" aria-label="Read our Accessibility Statement">
              Accessibility
            </Link>
          </motion.div>
          <motion.div whileHover={{ x: 2, color: "#5742bb" }}>
            <Link to="/cookie-policy" className="hover:text-brandPrimary" aria-label="Read our Cookie Policy">
              Cookie Policy
            </Link>
          </motion.div>
          <motion.div whileHover={{ x: 2, color: "#5742bb" }}>
            <Link to="/ai-ethics" className="hover:text-brandPrimary" aria-label="Read about our AI Ethics Framework">
              AI Ethics
            </Link>
          </motion.div>
          <p className="whitespace-nowrap">
            © {new Date().getFullYear()} Neuralward
          </p>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
